import React, { useState, useEffect, useRef } from 'react';
import { SearchBar } from './';
import MainLoader from '../Structures/MainLoader';
import { groupConsecutiveBySpeaker, highlightTextReact } from '../../utils/transcriptUtils';

const LiveTranscriptSection = ({ transcripts, loadingInfo, loadingTranscript, onGoToTimeStamp, initialTimestamp }) => {
  // Local states for search and auto-scroll functionality
  const [search, setSearch] = useState('');
  const [matchIndices, setMatchIndices] = useState([]);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [autoScroll, setAutoScroll] = useState(true);
  const [hasUserScrolled, setHasUserScrolled] = useState(false);
  const transcriptRef = useRef(null);
  const previousTranscriptLength = useRef(0);
  const [highlightedIndex, setHighlightedIndex] = useState(null);

  // Group the flat transcript by speaker using the helper function
  const groupedTranscripts = groupConsecutiveBySpeaker(transcripts);

  // Auto-scroll effect for transcript container
  useEffect(() => {
    if (autoScroll && transcriptRef.current && matchIndices.length === 0) {
      const container = transcriptRef.current;
      const hasNewContent = transcripts.length > previousTranscriptLength.current;
      if (!hasUserScrolled || (hasNewContent && autoScroll)) {
        container.scrollTop = container.scrollHeight;
        if (hasNewContent) {
          setHasUserScrolled(false);
        }
      }
    }
    previousTranscriptLength.current = transcripts.length;
  }, [transcripts, autoScroll, matchIndices, hasUserScrolled]);

  useEffect(() => {
    if (initialTimestamp && groupedTranscripts.length > 0) {
      const idx = groupedTranscripts.findIndex((group) => group.endTime >= initialTimestamp);
      const scrollToIdx = idx === -1 ? groupedTranscripts.length - 1 : idx;
      setHighlightedIndex(scrollToIdx);
      scrollToParagraph(scrollToIdx);
      setAutoScroll(false);
    }
  }, [initialTimestamp, groupedTranscripts.length]);

  // Run search when transcripts or search term change
  useEffect(() => {
    if (search || transcripts.length > 0) {
      handleSearch(search);
    }
  }, [transcripts, search]);

  // Scroll to the specific paragraph when current match index changes
  useEffect(() => {
    if (matchIndices.length > 0 && currentMatchIndex >= 0) {
      const targetParagraph = matchIndices[currentMatchIndex];
      scrollToParagraph(targetParagraph);
    }
  }, [currentMatchIndex, matchIndices]);

  // Handle search logic within grouped transcripts
  const handleSearch = (query) => {
    setSearch(query);
    if (!query) {
      setMatchIndices([]);
      setCurrentMatchIndex(0);
      if (autoScroll) {
        setHasUserScrolled(false);
      }
      return;
    }
    const matches = groupedTranscripts.reduce((acc, group, idx) => {
      if (group.text.toLowerCase().includes(query.toLowerCase())) {
        acc.push(idx);
      }
      return acc;
    }, []);
    setMatchIndices(matches);
    if (matches.length > 0) {
      setCurrentMatchIndex(0);
      setHasUserScrolled(true);
    } else {
      setCurrentMatchIndex(-1);
    }
  };

  // Toggle auto-scroll
  const handleAutoScrollToggle = (e) => {
    const isChecked = e.target.checked;
    setAutoScroll(isChecked);
    if (isChecked && transcriptRef.current) {
      setHasUserScrolled(false);
      transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
    }
  };

  // Navigation for search results (up/down)
  const onChangeLocation = (direction) => {
    if (matchIndices.length === 0) return;
    if (direction === 'down') {
      setCurrentMatchIndex((prev) => (prev + 1) % matchIndices.length);
    } else if (direction === 'up') {
      setCurrentMatchIndex((prev) => (prev - 1 + matchIndices.length) % matchIndices.length);
    }
  };

  // Scroll to a specific paragraph element by index
  const scrollToParagraph = (paragraphIndex) => {
    const paragraphElement = document.getElementById(`p-${paragraphIndex}`);
    const container = transcriptRef.current;
    if (paragraphElement && container) {
      container.scrollTo({
        top: paragraphElement.offsetTop - container.offsetTop,
        behavior: 'smooth',
      });
    }
  };

  // Render the grouped transcript: muestra cada grupo (speaker + texto) y agrega resaltado si hay búsqueda
  const renderTranscript = () => {
    return groupedTranscripts.map((group, i) => {
      const highlightedNodes = search ? highlightTextReact(group.text, search) : [group.text];
      const isHighlighted = i === highlightedIndex;
      return (
        <React.Fragment key={`transcript-${i}`}>
          <p
            id={`p-${i}`}
            className={`w-full cursor-pointer hover:bg-yellow-200 ${isHighlighted ? 'bg-yellow-100' : ''}`}
            onClick={() => onGoToTimeStamp(group.startTime)}
          >
            <strong className="font-bold">Speaker {group.speaker}:</strong> {highlightedNodes}
          </p>
          <br />
        </React.Fragment>
      );
    });
  };

  return (
    <div className="h-full max-h-[calc(100vh-220px)] flex-1 flex flex-col py-[15px] px-2.5 sm:px-3 border border-[#E5E7EB] bg-white">
      <div className="border-b border-[#E5E7EB] mb-2 pb-2 flex items-center justify-between">
        <h2 className="font-playfair text-[28px] capitalize text-darkblue font-medium leading-8">Live Transcript</h2>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input type="checkbox" checked={autoScroll} onChange={handleAutoScrollToggle} />
          Auto-scroll
        </label>
      </div>
      <div className="flex gap-3 pb-3">
        <SearchBar
          handleSearch={handleSearch}
          counter={matchIndices.length}
          location={matchIndices.length > 0 ? currentMatchIndex + 1 : 0}
          onChangeLocation={onChangeLocation}
          placeholder="Search transcript..."
          search={search}
          setSearch={setSearch}
          autoSearch
        />
        <button
          className="py-[9px] px-8 bg-[#ECEFF4] text-base font-normal text-black transition-all duration-500 hover:bg-lightblue-hover hover:text-white"
          onClick={() => handleSearch(search)}
        >
          Search
        </button>
      </div>
      <div className="w-full overflow-y-auto overflow-x-hidden flex flex-col" ref={transcriptRef}>
        {loadingInfo || loadingTranscript ? <MainLoader /> : renderTranscript()}
      </div>
    </div>
  );
};

export default LiveTranscriptSection;
